import React from "react";
import { Modal } from "react-bootstrap";

import { CurrencyRef } from "../../../../models/WorkshopDTO";
import { WorkshopPreviewDTO } from "../../../../services/v2/workshops.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { montoBonito } from "../../../../utils/monto";
import { horaBonita } from "./comun";
import S from "./strings";
import "./talleres.css";

/*
	Talleres y Personalizados (Módulos Claros, 2026-11-01): the small shared pieces of the module. The window and the
	«Qué Va a Pasar» confirmation follow Promociones (detallePromo.tsx) and Actualizaciones (ConfirmarAplicar).
*/

const DIA_CORTO = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const deYmd = (s: string): Date => { const [y, m, d] = s.substring(0, 10).split("-").map(Number); return new Date(y, m - 1, d); };

/** Money with the campus symbol: "L 1,200.00". */
export const monto = (cur: CurrencyRef | { symbol?: string | null } | null | undefined, v: any): string => `${cur?.symbol || ""} ${montoBonito(v)}`.trim();

/** "2026-11-08" -> "08/11/2026". */
export const fechaDMY = (s: string | null | undefined): string => {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
	return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
/** "2026-11-08" -> "08/11" (grid columns). */
export const diaMes = (s: string): string => fechaDMY(s).substring(0, 5);
/** "2026-11-08" -> "Sáb 08/11/2026". */
export const diaSemanaFecha = (s: string): string => `${DIA_CORTO[deYmd(s).getDay()]} ${fechaDMY(s)}`;
/** "2026-11-08" -> "Sáb 08/11" (grid column header). */
export const diaSemanaCorto = (s: string): string => `${DIA_CORTO[deYmd(s).getDay()]} ${diaMes(s)}`;

/** "09:00" + "13:00" -> "9:00 am a 1:00 pm". */
export const rangoHoras = (a: string | null | undefined, b: string | null | undefined): string =>
	horaBonita(a) + (b ? " a " + horaBonita(b) : "");

/**
 * The days of a workshop in one short line: "Sáb 8, 15 y 22 nov" when there are few in one month,
 * "26 oct al 6 nov" otherwise (the count goes in the second line).
 */
export const diasTexto = (fechas: string[]): string => {
	if (!fechas.length) return S.sinDias;
	const ds = fechas.map(deYmd);
	const a = ds[0], b = ds[ds.length - 1];
	if (ds.length === 1) return `${DIA_CORTO[a.getDay()]} ${a.getDate()} ${MES_CORTO[a.getMonth()]}`;
	const mismoMes = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
	if (ds.length <= 4 && mismoMes) {
		const mismoDia = ds.every((d) => d.getDay() === a.getDay());
		const nums = ds.map((d) => String(d.getDate()));
		return `${mismoDia ? DIA_CORTO[a.getDay()] + " " : ""}${nums.slice(0, -1).join(", ")} y ${nums[nums.length - 1]} ${MES_CORTO[a.getMonth()]}`;
	}
	return `${a.getDate()} ${MES_CORTO[a.getMonth()]} al ${b.getDate()} ${MES_CORTO[b.getMonth()]}`;
};

export const hoyYmd = (): string => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Chip in the wdChip style: neutral, acento or oro (never green). */
export const Chip: React.FC<{ tono?: "oro" | "acento" | "neutro"; children: React.ReactNode; title?: string }> = ({ tono, children, title }) => (
	<span className={"wtlChip " + (tono || "neutro")} title={title}>{children}</span>
);

/** Workshop state from its dates: Próximo, En Curso, Terminado, Anulado. */
export const ChipEstado: React.FC<{ tab?: string | null }> = ({ tab }) =>
	tab ? <Chip tono={tab === "anulados" ? "oro" : tab === "enCurso" ? "acento" : "neutro"}>{S.estadoTab[tab] || tab}</Chip> : null;

/** Window like Promociones: title, body and the row of buttons below. */
export const Ventana: React.FC<{ titulo: React.ReactNode; onClose: () => void; busy?: boolean; pie?: React.ReactNode; size?: "sm" | "lg" | "xl"; children: React.ReactNode; className?: string }> =
	({ titulo, onClose, busy, pie, size, children, className }) => {
		const movil = useEsTelefono();
		return (
			<Modal className={"waModulo wtlVentana " + (className || "")} show onHide={busy ? undefined : onClose} size={size || "lg"} centered scrollable fullscreen="sm-down">
				<Modal.Header closeButton={!busy}>
					<Modal.Title style={{ fontSize: movil ? 16 : 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{titulo}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>{children}</Modal.Body>
				{pie ? <Modal.Footer className="wtlPie">{pie}</Modal.Footer> : null}
			</Modal>
		);
	};

/**
 * «Qué Va a Pasar»: what the server says the action will do, per person, before doing it. People that would be skipped
 * (already enrolled, full, with payments) are listed in gold; with nobody to change, Confirmar stays off.
 */
export const QueVaAPasar: React.FC<{ vista: WorkshopPreviewDTO; boton: string; busy?: boolean; error?: string | null; onCancel: () => void; onConfirm: () => void;
	bloqueaTodo?: boolean }> = ({ vista, boton, busy, error, onCancel, onConfirm, bloqueaTodo }) => {
	const s = { symbol: vista.currencySymbol };
	// Cancelling a workshop with any payment is refused by the server as a whole: no partial cancel.
	const puede = !busy && (bloqueaTodo ? vista.blocked === 0 : vista.ok > 0 || (vista.people.length === 0 && vista.action !== "inscribir"));
	return (
		<Modal className="waModulo wtlVentana" show onHide={busy ? undefined : onCancel} centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!busy}><Modal.Title as="h5">{S.queVaAPasar}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left" }}>
				<div className="wtlSub" style={{ marginBottom: 8 }}><b style={{ color: "var(--wa-tinta)" }}>{vista.workshopName}</b></div>
				<div className="wau-confirma">
					{vista.people.map((p) => (
						<div className="persona" key={"p" + p.studentId}>
							<b>{p.name}</b>
							<div className="wau-efectos">{p.lines.map((l, j) => <div key={"l" + j} className={p.blocked ? "wau-oro" : ""}>{l}</div>)}</div>
						</div>
					))}
				</div>
				{vista.notes.map((n, i) => <div key={"n" + i} className="wtlNota">{n}</div>)}
				{vista.total > 0 ? <div className="wtlTotal"><span>{S.total}</span><b>{monto(s, vista.total)}</b></div> : null}
				{error ? <div className="waAviso" role="alert" style={{ marginTop: 10 }}>{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>{S.volver}</button>
				<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!puede} onClick={onConfirm}>{busy ? S.cargando : boton}</button>
			</Modal.Footer>
		</Modal>
	);
};
